import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';

import { canSearchPatientLookup, canSearchPatientUhid, PATIENT_UHID_INPUT_PLACEHOLDER } from '../../lib/patient-uhid-search';
import { normalizePhoneInput, PHONE_INPUT_MAX_LENGTH, PHONE_INPUT_PATTERN, PHONE_INPUT_TITLE } from '../../lib/phone-input';
import './patient-uhid-autocomplete.css';

export interface PatientUhidAutocompleteOption {
  id: string;
  label: string;
  code?: string;
  phone?: string;
  gender?: string;
  dateOfBirth?: string;
}

interface PatientUhidAutocompleteProps {
  id: string;
  value: string;
  selectedId?: string | null;
  options?: readonly PatientUhidAutocompleteOption[] | undefined;
  isFetching?: boolean;
  isError?: boolean;
  disabled?: boolean;
  required?: boolean;
  ariaInvalid?: boolean;
  matchInputWidth?: boolean;
  displaySelectedName?: boolean;
  mode?: 'uhid' | 'name-phone' | 'name' | 'phone' | 'uhid-name-phone';
  maxLength?: number;
  pattern?: string;
  inputMode?: 'text' | 'numeric' | 'tel';
  placeholder?: string;
  title?: string;
  onInputChange: (value: string) => void;
  onChange: (patientId: string) => void;
}

const patientGenderLabel = (value: string | undefined): string => {
  if (value === 'male') return 'Male';
  if (value === 'female') return 'Female';
  if (value === 'other') return 'Others';
  return value ?? '';
};

const patientAgeLabel = (value: string | undefined): string => {
  if (!value) return '';
  const birth = new Date(value);
  if (Number.isNaN(birth.getTime())) return '';
  const today = new Date();
  let years = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth() - birth.getMonth();
  if (today.getDate() < birth.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  return `${Math.max(0, years)}Y ${Math.max(0, months)}M`;
};

export const PatientUhidAutocomplete = ({
  id,
  value,
  selectedId,
  options = [],
  isFetching = false,
  isError = false,
   disabled = false,
   required = false,
   ariaInvalid = false,
   matchInputWidth = false,
   displaySelectedName = false,
   mode = 'uhid',
  maxLength = 30,
  pattern,
  inputMode,
  placeholder: placeholderOverride,
  title,
  onInputChange,
  onChange,
}: PatientUhidAutocompleteProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [panelStyle, setPanelStyle] = useState<CSSProperties | null>(null);
  const [lastSelectedOption, setLastSelectedOption] = useState<PatientUhidAutocompleteOption | null>(null);
  const searchable = mode === 'uhid' ? canSearchPatientUhid(value) : canSearchPatientLookup(value);
  const phoneMode = mode === 'phone';
  const showPanel = open && searchable && !disabled;
  const selectedOption = options.find((option) => option.id === selectedId)
    ?? (lastSelectedOption?.id === selectedId ? lastSelectedOption : undefined);
  const selectedDisplay = selectedOption
    ? displaySelectedName
      ? selectedOption.label
      : mode === 'phone'
      ? selectedOption.phone ?? value
      : mode === 'name'
        ? selectedOption.label
        : mode === 'name-phone'
          ? [selectedOption.label, selectedOption.phone].filter(Boolean).join(' · ')
          : selectedOption.code ?? selectedOption.label
    : value;
  const inputValue = selectedId ? selectedDisplay : value;
  const placeholder = placeholderOverride ?? (mode === 'phone'
    ? 'Type patient phone number'
    : mode === 'name'
      ? 'Type patient name'
      : mode === 'name-phone'
        ? 'Type patient name or phone number'
        : mode === 'uhid-name-phone'
          ? 'Type UHID, patient name, or phone number'
          : PATIENT_UHID_INPUT_PLACEHOLDER);

  useLayoutEffect(() => {
    if (!showPanel) return;
    const updatePosition = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < 230 && rect.top > spaceBelow;
      const width = matchInputWidth
        ? rect.width
        : Math.min(Math.max(rect.width * 2, rect.width), window.innerWidth - 16);
      const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - width - 8));
      setPanelStyle({
        position: 'fixed',
        left,
        width,
        ...(openUpward ? { bottom: window.innerHeight - rect.top + 5 } : { top: rect.bottom + 5 }),
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [matchInputWidth, showPanel]);

  useEffect(() => {
    setActiveIndex(0);
  }, [options, value]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [open]);

  const select = (option: PatientUhidAutocompleteOption) => {
    if (mode === 'uhid' || mode === 'uhid-name-phone') onInputChange(option.code ?? option.label);
    setLastSelectedOption(option);
    onChange(option.id);
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, Math.max(0, options.length - 1)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(0, index - 1));
    } else if (event.key === 'Enter' && showPanel) {
      const option = options[activeIndex];
      if (!option) return;
      event.preventDefault();
      select(option);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const clear = () => {
    onInputChange('');
    onChange('');
    setLastSelectedOption(null);
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={rootRef} className={`patient-uhid-autocomplete${selectedId ? ' selected' : ''}`}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showPanel}
        aria-controls={`${id}-listbox`}
        aria-activedescendant={showPanel && options[activeIndex] ? `${id}-option-${activeIndex}` : undefined}
        value={inputValue}
        placeholder={placeholder}
        maxLength={phoneMode ? Math.min(maxLength, PHONE_INPUT_MAX_LENGTH) : maxLength}
        pattern={phoneMode ? pattern ?? PHONE_INPUT_PATTERN : pattern}
        inputMode={phoneMode ? inputMode ?? 'numeric' : inputMode}
        title={phoneMode ? title ?? PHONE_INPUT_TITLE : title}
        autoComplete="off"
        disabled={disabled}
        required={required}
        aria-invalid={ariaInvalid || undefined}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onChange={(event) => {
          onInputChange(phoneMode ? normalizePhoneInput(event.target.value) : event.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      {isFetching ? <span className="patient-uhid-autocomplete-spinner" aria-label="Searching" /> : null}
      {selectedId && !disabled ? <button type="button" className="patient-uhid-autocomplete-clear" aria-label="Clear patient" onClick={clear}>×</button> : null}

      {showPanel && panelStyle ? (
        <div className="patient-uhid-autocomplete-panel" style={panelStyle}>
          {isFetching ? <div className="patient-uhid-autocomplete-message">Searching patients…</div>
            : isError ? <div className="patient-uhid-autocomplete-message error">Patient search could not be loaded.</div>
              : options.length === 0 ? <div className="patient-uhid-autocomplete-message">{mode === 'uhid' ? 'No matching UHID found.' : 'No matching patient found.'}</div>
                : (
                  <ul id={`${id}-listbox`} role="listbox">
                    {options.map((option, index) => (
                      <li
                        id={`${id}-option-${index}`}
                        key={option.id}
                        role="option"
                        aria-selected={option.id === selectedId}
                        className={`${index === activeIndex ? 'active' : ''}${option.id === selectedId ? ' selected' : ''}`}
                        onMouseEnter={() => setActiveIndex(index)}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={(event) => { event.preventDefault(); select(option); }}
                      >
                        <div className="patient-uhid-autocomplete-option-head">
                          <strong>{option.label}</strong>
                          <span>{option.code ?? 'UHID'}</span>
                        </div>
                        <small>{[option.phone, patientGenderLabel(option.gender), patientAgeLabel(option.dateOfBirth)].filter(Boolean).join(' - ')}</small>
                        {option.id === selectedId ? <b aria-hidden="true">OK</b> : null}
                      </li>
                    ))}
                  </ul>
                )}
        </div>
      ) : null}
    </div>
  );
};
